var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CardCarousel_1;
import { injectable } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { stateable } from '@slickdeals/durango/dist/javascript/decorators/stateable';
import { debounce } from '@slickdeals/durango/dist/javascript/utilities/debounce';
import BlueprintComponent from '../core/blueprintComponent';
import { autoplayable } from '../decorators/autoplayable';
import { CSS_CONTENTS_CLASSNAME, } from '../utilities/autoPlayableController';
import './icon';
import './iconButton';
import '../../scss/components/cardCarousel.scss';
/** Name of the component */
export const name = 'CardCarousel';
export var ClassNames;
(function (ClassNames) {
    ClassNames["Default"] = "bp-c-cardCarousel";
    ClassNames["JavaScript"] = "js-cardCarousel";
    ClassNames["Track"] = "js-cardCarousel_track";
    ClassNames["TrackInner"] = "js-cardCarousel_trackInner";
    ClassNames["PreviousButton"] = "js-cardCarousel_btn--prev";
    ClassNames["NextButton"] = "js-cardCarousel_btn--next";
    ClassNames["Hide"] = "hide";
    ClassNames["Disable"] = "disable";
})(ClassNames || (ClassNames = {}));
let CardCarousel = CardCarousel_1 = class CardCarousel extends BlueprintComponent {
    /** Implicit constructor that calls the init function and defaults state */
    constructor(options = {
        autoplayOptions: { autoplay: true, cycleContent: true, delay: 8000 },
    }) {
        super(name, options);
        this.options = options;
        // Define the initial state
        const initialState = {
            currentPage: 0,
            pagePositions: ['0%'],
        };
        // Initialize the component state
        this.initState(initialState);
    }
    /** Named constructor */
    static create(options = {}) {
        return new CardCarousel_1(options);
    }
    /** Initializes the object once the state comes up for setup */
    async init() {
        super.init();
        this.transitionCallback = this.autoplayableTransition.bind(this);
        this.element = await BlueprintComponent.loadElement(name, this.options);
        this.track = this.element.querySelector(`.${ClassNames.Track}`);
        this.trackInner = this.element.querySelector(`.${ClassNames.TrackInner}`);
        this.cards = this.trackInner.children;
        for (const card of this.cards) {
            card.classList.add(CSS_CONTENTS_CLASSNAME);
        }
        this.nextButton = this.element.querySelector(`.${ClassNames.NextButton}`);
        this.previousButton = this.element.querySelector(`.${ClassNames.PreviousButton}`);
        this.nextButton.addEventListener('click', this.nextSlides.bind(this));
        this.previousButton.addEventListener('click', this.prevSlides.bind(this));
        this.setPagePositions();
        this.resizeObserver = new ResizeObserver(debounce(100, this.resizeSlides.bind(this)));
        this.resizeObserver.observe(this.track);
        this.resizeObserver.observe(this.trackInner);
        // Bind events for cycle pausing/resuming
        this.element.addEventListener('mouseenter', this.stopAutoplay.bind(this));
        this.element.addEventListener('mouseleave', this.startAutoplay.bind(this));
    }
    static createFromDataAttributes(element) {
        // need to fetch different attributes
        const autoplayOptions = JSON.parse(element.dataset.autoplayoptions);
        const options = {
            id: element.id,
            autoplayOptions,
        };
        return new CardCarousel_1(options);
    }
    async moveSlides(container, direction) {
        let newPage = this.getState().currentPage + direction;
        const { pagePositions } = this.getState();
        if (newPage < 0) {
            newPage = pagePositions.length - 1;
        }
        else if (newPage >= pagePositions.length) {
            newPage = 0;
        }
        this.setState({ currentPage: newPage });
        this.track.style.transform = `translateX(-${pagePositions[newPage]}px)`;
        this.previousButton.classList.toggle(ClassNames.Disable, newPage === 0);
        this.nextButton.classList.toggle(ClassNames.Disable, newPage >= pagePositions.length - 1);
    }
    autoplayableTransition() {
        return (count) => {
            this.moveSlides(this.element, 1);
            return count;
        };
    }
    async nextSlides() {
        this.moveSlides(this.element, 1);
    }
    async prevSlides() {
        this.moveSlides(this.element, -1);
    }
    async setPagePositions() {
        const pagePositions = [0];
        const trackWidth = this.track.offsetWidth;
        for (let i = 0; i < this.cards.length; i += 1) {
            const card = this.cards[i];
            const cardRight = card.offsetLeft + card.offsetWidth;
            // using if/else statements because the no-continue linting rules
            if (cardRight - pagePositions[pagePositions.length - 1] > trackWidth) {
                if (i < this.cards.length - 1) {
                    pagePositions.push(card.offsetLeft);
                }
                else {
                    // last element
                    pagePositions.push(this.trackInner.offsetWidth - trackWidth);
                }
            }
            else if (i >= this.cards.length - 1) {
                // last index of cards and it's not touching right edge of track, move the last page position to touch right edge
                pagePositions[pagePositions.length - 1] = this.trackInner.offsetWidth - trackWidth;
            }
        }
        // if going to a bigger size and the current page exceeds number of pages at new breakpoint
        const { currentPage: oldPage } = this.getState();
        this.setState({
            pagePositions,
            currentPage: (oldPage >= pagePositions.length ? pagePositions.length - 1 : oldPage),
        });
        const hideButtons = pagePositions.length < 2;
        this.nextButton.classList.toggle(ClassNames.Hide, hideButtons);
        this.previousButton.classList.toggle(ClassNames.Hide, hideButtons);
    }
    /** Handles the transition to/from desktop */
    async resizeSlides() {
        this.setPagePositions();
        this.moveSlides(this.element, 0);
    }
};
CardCarousel = CardCarousel_1 = __decorate([
    stateable(name),
    injectable(name),
    autoplayable()
], CardCarousel);
export default CardCarousel;
// Create any CardCarousel components in the hydration queue
BlueprintComponent.instantiate(name, {
    fromHydrationQueue: true,
    fromDom: true,
    fromMutation: true,
});
